import { useFeature } from '@acorns/web-toggles-react'
import { getWindow } from '@acorns/web-utils'
import React, { createContext, useState, useEffect, useContext } from 'react'

import CenteredSpinner from 'components/CenteredSpinner'
import { acornsEarlyGoldPrefill } from 'constants/feature-keys'
import {
  ExternalSubscriptionType,
  useUserMatchesQuery,
  useExchangeAcornsEarlyTokenMutation,
  useUpsertUserMatchMutation,
  useUpdateEarlyCustomerInfoMutation,
  useUpdateEarlyCustomerAddressMutation,
  ExternalUserSource,
  AddressType,
} from 'generated/types'

import { validate as validateAddress, ZIP_CODE_LENGTH } from 'pages/ContactInfo/form'
import { validate as validatePersonalInfo } from 'pages/PersonalInfo/form'
import { isOneTimeToken } from 'pages/Signup/logical/queryParams'
import { validate as validateEmail } from 'pages/Signup/logical/validation'
import { EnvironmentProps, withEnv } from 'utils/environment'
import { getSourceAttributes } from 'utils/sourceAttributes'

import { ResumeFlowContext } from '../ResumeFlowProvider'
import { FlowRoute } from 'utils/FlowProvider/types'
import { withApolloErrorHandler } from 'utils/Apollo'

export interface AcornsEarlyContextProps {
  isAcornsEarlyGoldCustomerFlow: boolean
  earlyCustomerEmail: string | null
  saveEarlyCustomerInfo: () => Promise<void>
  earlySubscriptionType: ExternalSubscriptionType | null
}

export const AcornsEarlyContext = createContext<AcornsEarlyContextProps | null>(null)

const extractValidEarlyCustomerEmail = (data: { __typename?: string; email?: string } = {}) => {
  const { __typename, email } = data
  if (__typename === 'AcornsEarlyUserDetails' && !('email' in validateEmail({ email }))) {
    return email
  }
  return null
}

const AcornsEarlyProviderComponent = ({
  env,
  children,
}: { children: React.ReactNode } & EnvironmentProps) => {
  const [isAcornsEarlyGoldCustomer, setIsAcornsEarlyGoldCustomer] = useState(false)
  const [isEarlyGoldPrefillEnabled] = useFeature(acornsEarlyGoldPrefill)
  const { isResumeFlow } = useContext(ResumeFlowContext)
  const shouldSkip = !isEarlyGoldPrefillEnabled || !isResumeFlow
  const [earlyCustomerEmail, setEarlyCustomerEmail] = useState<string | null>(null)
  const [earlySubscriptionType, setEarlySubscriptionType] =
    useState<ExternalSubscriptionType | null>(null)

  const [
    exchangeOneTimeToken,
    {
      data: tokenExchangeData,
      loading: isLoadingTokenExchangeData,
      called: hasExchangeOneTimeTokenBeenCalled,
    },
  ] = useExchangeAcornsEarlyTokenMutation({
    variables: {
      token: getSourceAttributes().oneTimeToken,
    },
  })
  const {
    data: userMatchesData,
    loading,
    error,
  } = useUserMatchesQuery({
    skip: shouldSkip,
    fetchPolicy: 'no-cache',
  })
  const [upsertUserMatch] = useUpsertUserMatchMutation({
    fetchPolicy: 'no-cache',
    onError: () => null,
  })
  const [updateEarlyCustomerInfo] = useUpdateEarlyCustomerInfoMutation({
    fetchPolicy: 'no-cache',
    onError: () => null,
  })
  const [updateEarlyCustomerAddress] = useUpdateEarlyCustomerAddressMutation({
    fetchPolicy: 'no-cache',
    onError: () => null,
  })

  const setCustomerMatch = (data = userMatchesData) => {
    if (!data?.userMatches) return

    const activeMatch = data.userMatches.find(
      (match) =>
        match.externalMetadata?.isSubscriptionActive === true &&
        (match.externalMetadata?.membership === ExternalSubscriptionType.PREMIUM ||
          match.externalMetadata?.membership === ExternalSubscriptionType.LEGACY_PREMIUM),
    )

    if (isResumeFlow) {
      const hasEarlyPremiumMembership = !!activeMatch
      setIsAcornsEarlyGoldCustomer(hasEarlyPremiumMembership)
    }

    setEarlySubscriptionType(activeMatch?.externalMetadata?.membership)
  }

  // In the first-time signup flow, handle token exchange
  useEffect(() => {
    const isSignupPage = window.location.pathname === FlowRoute.Signup
    const shouldSkipTokenExchange =
      !isSignupPage ||
      !isOneTimeToken() ||
      !isEarlyGoldPrefillEnabled ||
      hasExchangeOneTimeTokenBeenCalled ||
      isLoadingTokenExchangeData ||
      isResumeFlow
    if (shouldSkipTokenExchange) {
      return
    }

    const handleEarlyGoldCustomer = async () => {
      const { data } = await exchangeOneTimeToken()
      const customerDetails = data?.exchangeAcornsEarlyToken
      if (customerDetails?.__typename !== 'AcornsEarlyUserDetails') {
        return
      }
      if (customerDetails.credentialsExist) {
        getWindow().location.assign(env.get('loginUrl'))
        return
      }
      const email = extractValidEarlyCustomerEmail(customerDetails)
      if (email) {
        setEarlyCustomerEmail(email)
        setIsAcornsEarlyGoldCustomer(true)
        return
      }
    }
    handleEarlyGoldCustomer()
  }, [])

  // In the resume flow, check if the customer has a premium Early membership
  useEffect(() => {
    if (loading || error || !userMatchesData?.userMatches || !isResumeFlow) {
      return
    }
    setCustomerMatch()
  }, [loading, error, userMatchesData?.userMatches, isResumeFlow])

  const saveEarlyCustomerInfo = async () => {
    if (!isAcornsEarlyGoldCustomer || !isEarlyGoldPrefillEnabled) {
      return
    }

    const earlyCustomerInfo = tokenExchangeData?.exchangeAcornsEarlyToken
    if (earlyCustomerInfo?.__typename !== 'AcornsEarlyUserDetails') {
      return
    }

    const personalInfoValidationErrors = validatePersonalInfo({
      firstName: earlyCustomerInfo?.firstName,
      lastName: earlyCustomerInfo?.lastName,
      phoneNumber: earlyCustomerInfo?.phoneNumber,
    })

    const formattedAddress = {
      ...earlyCustomerInfo,
      zipCode: earlyCustomerInfo.zipCode.slice(0, ZIP_CODE_LENGTH),
    }
    const addressValidationErrors = validateAddress({
      address1: formattedAddress?.address1,
      address2: formattedAddress?.address2,
      city: formattedAddress?.city,
      state: formattedAddress?.state,
      zip: formattedAddress?.zipCode,
    })

    const personalInfoInput = {
      ...(!('firstName' in personalInfoValidationErrors) && {
        firstName: earlyCustomerInfo.firstName,
      }),
      ...(!('lastName' in personalInfoValidationErrors) && {
        lastName: earlyCustomerInfo.lastName,
      }),
      ...(!('phoneNumber' in personalInfoValidationErrors) && {
        phoneNumber: earlyCustomerInfo.phoneNumber,
      }),
    }

    const results = await Promise.allSettled([
      upsertUserMatch({
        variables: {
          input: {
            externalId: earlyCustomerInfo.externalId,
            externalSource: ExternalUserSource.USER_EXTERNAL_SOURCE_GO_HENRY,
            matchedCriteria: ['GH_ACORNS_LINK_ACCOUNT_FLOW'],
          },
        },
      }),
      Object.keys(personalInfoInput).length > 0 &&
        updateEarlyCustomerInfo({ variables: personalInfoInput }),
      Object.keys(addressValidationErrors).length === 0 &&
        updateEarlyCustomerAddress({
          variables: {
            addressType: AddressType.residential,
            input: {
              street1: formattedAddress.address1,
              street2: formattedAddress?.address2 || '',
              city: formattedAddress.city,
              state: formattedAddress.state,
              zipCode: formattedAddress.zipCode,
              country: 'US',
            },
          },
        }),
    ])

    const [upsertResult] = results
    if (upsertResult.status === 'fulfilled') {
      const match = upsertResult.value.data?.upsertUserMatch
      if (match?.__typename === 'UserMatch') {
        setCustomerMatch({ userMatches: [match] })
      }
    }
  }

  return (
    <AcornsEarlyContext.Provider
      value={{
        isAcornsEarlyGoldCustomerFlow: isEarlyGoldPrefillEnabled && isAcornsEarlyGoldCustomer,
        earlyCustomerEmail,
        saveEarlyCustomerInfo,
        earlySubscriptionType,
      }}
    >
      {isLoadingTokenExchangeData ? <CenteredSpinner /> : children}
    </AcornsEarlyContext.Provider>
  )
}

const withSuppressApolloErrorHandlers = (operations: string[]) => {
  return (Component: any) =>
    operations.reduce(
      (component, operation) => withApolloErrorHandler(operation, () => () => false)(component),
      Component,
    )
}

export const AcornsEarlyProvider = withSuppressApolloErrorHandlers([
  'ExchangeAcornsEarlyToken',
  'UserMatches',
  'UpsertUserMatch',
  'UpdateEarlyCustomerInfo',
  'UpdateEarlyCustomerAddress',
])(withEnv(AcornsEarlyProviderComponent as any))
